import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { ListItemButton, ListItemIcon, ListItemText, type SxProps } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useViewMode } from "@/hooks/useViewMode";
import { UserView } from "@/utils/models";

// The "Switch to Admin/Staff view" row, shared by both drawers. Local (not UIToolkit) since it
// bakes in feature logic via useViewMode rather than being purely prop-driven. Styling is passed
// in as props since the two drawers style their rows differently. Renders nothing for single-role users.
export interface ViewSwitchMenuItemProps {
	/** Close the drawer before the switch triggers a reload. */
	onBeforeSwitch: () => void;
	dataTestId: string;
	buttonSx?: SxProps;
	iconContainerSx?: SxProps;
	iconSx?: SxProps;
	textSx?: SxProps;
	/** Show the "current view" line under the label. */
	showCurrentView?: boolean;
}

export const ViewSwitchMenuItem: FC<ViewSwitchMenuItemProps> = ({ onBeforeSwitch, dataTestId, buttonSx, iconContainerSx, iconSx, textSx, showCurrentView }) => {
	const { t } = useTranslation();
	const { view, canSwitchView, isSwitching, switchView } = useViewMode();

	if (!canSwitchView) return null;

	const isAdminView = view === UserView.admin;
	// Primary label names the destination view; secondary shows the current one.
	const primary = t(isAdminView ? "switch.to.staff.view" : "switch.to.admin.view");
	const currentViewLabel = t(isAdminView ? "currently.in.admin.view" : "currently.in.staff.view");

	return (
		<ListItemButton
			data-testid={dataTestId}
			onClick={() => {
				onBeforeSwitch();
				switchView();
			}}
			disabled={isSwitching}
			sx={buttonSx}
		>
			<ListItemIcon sx={iconContainerSx}>
				<SwapHorizIcon sx={iconSx} />
			</ListItemIcon>
			<ListItemText primary={primary} secondary={showCurrentView ? currentViewLabel : undefined} sx={textSx} />
		</ListItemButton>
	);
};
